import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import type { PracticeOrgAdmin, PracticeOrgAdminDetail, PracticeUser } from "../types";
import { Icons } from "../utils/icons";
import { esc } from "../utils/html";

let orgs: PracticeOrgAdmin[] = [];

const ROLE_LABELS: Record<string, string> = {
  "praktikumsstelle-admin": "Admin",
  "praktikumsstelle": "Mitarbeiter",
};

const CATEGORY_LABELS: Record<string, string> = {
  "PRAXIS": "Praxis",
  "KRANKENHAUS": "Krankenhaus",
};

async function load() {
  orgs = await api.admin.practices.list();
}

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("#org-tbody")!;
  if (orgs.length === 0) {
    tbody.innerHTML = `<tr><td colspan="8" class="empty-state">Keine Praktikumsstellen vorhanden</td></tr>`;
    return;
  }
  tbody.innerHTML = orgs.map(o => {
    const statusBadge = o.active
      ? `<span class="badge badge-green">Aktiv</span>`
      : `<span class="badge" style="background:var(--red-50);color:var(--red-700)">Inaktiv</span>`;
    const created = new Date(o.created_at).toLocaleDateString("de-DE");
    const cat = CATEGORY_LABELS[o.category] || o.category;
    const linkedCount = o.linkedSchoolsCount ?? 0;
    return `<tr data-id="${o.id}" style="${!o.active ? 'opacity:.55' : ''}">
      <td style="font-weight:600">${esc(o.name)}</td>
      <td><code style="font-size:.8125rem;background:var(--gray-100);padding:.125rem .375rem;border-radius:.25rem">${esc(o.code)}</code></td>
      <td class="text-muted">${esc(cat)}</td>
      <td class="text-muted">${esc(o.ort || "–")}</td>
      <td style="text-align:center">${statusBadge}</td>
      <td class="text-muted" style="text-align:center">${linkedCount}</td>
      <td class="text-muted">${created}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-sm btn-secondary btn-users" data-id="${o.id}" title="Benutzer verwalten">Benutzer</button>
        <button class="btn btn-sm btn-secondary btn-toggle" data-id="${o.id}" title="${o.active ? "Deaktivieren" : "Aktivieren"}">${o.active ? Icons.pauseCircle : Icons.playCircle}</button>
        <button class="btn btn-danger btn-del" data-id="${o.id}" title="Löschen">${Icons.trash}</button>
      </td></tr>`;
  }).join("");

  tbody.querySelectorAll("tr[data-id]").forEach(tr => {
    tr.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".btn-toggle") || target.closest(".btn-del") || target.closest(".btn-users")) return;
      const org = orgs.find(o => o.id == Number(tr.getAttribute("data-id")));
      if (org) openForm(org);
    });
  });

  tbody.querySelectorAll(".btn-users").forEach(btn => btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const id = Number((btn as HTMLElement).dataset.id);
    const org = orgs.find(o => o.id === id);
    if (org) openUsersModal(org);
  }));

  tbody.querySelectorAll(".btn-toggle").forEach(btn => btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const id = Number((btn as HTMLElement).dataset.id);
    const org = orgs.find(o => o.id === id);
    if (!org) return;
    const action = org.active ? "deaktivieren" : "aktivieren";
    if (!confirm(`Praktikumsstelle „${org.name}" wirklich ${action}?`)) return;
    try {
      await api.admin.practices.toggleActive(id);
      await load();
      renderTable(el);
    } catch (err: any) {
      alert("Fehler: " + err.message);
    }
  }));

  tbody.querySelectorAll(".btn-del").forEach(btn => btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const id = Number((btn as HTMLElement).dataset.id);
    const org = orgs.find(o => o.id === id);
    if (!org) return;
    if (!confirm(`Praktikumsstelle „${org.name}" wirklich löschen?\n\nDies ist nur möglich, wenn die Praktikumsstelle mit keiner Schule verknüpft ist.`)) return;
    try {
      await api.admin.practices.delete(id);
      await load();
      renderTable(el);
    } catch (err: any) {
      alert(err.message);
    }
  }));
}

async function openForm(org?: PracticeOrgAdmin) {
  const isEdit = !!org;
  let detail: PracticeOrgAdminDetail | null = null;
  if (isEdit) {
    try { detail = await api.admin.practices.get(org!.id); }
    catch (err: any) { alert("Fehler beim Laden: " + err.message); return; }
  }

  const linkedSchoolsHtml = detail && detail.linkedSchools.length > 0
    ? `<ul style="margin:.25rem 0 0;padding-left:1.2rem;font-size:.875rem">
        ${detail.linkedSchools.map(s => `<li>${esc(s.schoolName)} <span style="color:var(--gray-400)">(${esc(s.schoolSlug)})</span></li>`).join("")}
       </ul>`
    : `<div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">Keine Schule verknüpft.</div>`;

  const catVal = isEdit ? (org!.category || "PRAXIS") : "PRAXIS";

  openModal(isEdit ? "Praktikumsstelle bearbeiten" : "Neue Praktikumsstelle anlegen", `
    <div class="form-grid">
      <div class="form-group col-span-2"><label>Name *</label><input id="f-name" value="${esc(org?.name || "")}" placeholder="z.B. Praxis Dr. Müller" /></div>
      <div class="form-group"><label>Kategorie *</label><select id="f-category">
        <option value="PRAXIS" ${catVal === "PRAXIS" ? "selected" : ""}>Praxis</option>
        <option value="KRANKENHAUS" ${catVal === "KRANKENHAUS" ? "selected" : ""}>Krankenhaus</option>
      </select></div>
      <div class="form-group"><label>Ansprechperson</label><input id="f-contact" value="${esc(org?.contact_person || "")}" /></div>
      <div class="form-group"><label>Straße</label><input id="f-street" value="${esc(org?.street || "")}" /></div>
      <div class="form-group"><label>Hausnummer</label><input id="f-houseno" value="${esc(org?.house_no || "")}" /></div>
      <div class="form-group"><label>PLZ *</label><input id="f-plz" value="${esc(org?.plz || "")}" /></div>
      <div class="form-group"><label>Ort *</label><input id="f-ort" value="${esc(org?.ort || "")}" /></div>
      <div class="form-group"><label>E-Mail *</label><input id="f-email" type="email" value="${esc(org?.email || "")}" /></div>
      <div class="form-group"><label>Telefon</label><input id="f-phone" value="${esc(org?.phone || "")}" /></div>
    </div>
    ${isEdit ? `<fieldset style="margin-top:1rem"><legend>System-Informationen</legend>
      <div style="font-size:.875rem;color:var(--gray-600);line-height:1.7">
        <div><strong>Code:</strong> <code>${esc(org!.code)}</code></div>
        <div><strong>Slug:</strong> <code>${esc(org!.slug)}</code></div>
        <div><strong>Keycloak-Gruppe:</strong> <code>${esc(org!.keycloakGroup)}</code></div>
        <div><strong>AGB akzeptiert:</strong> ${org!.agbAcceptedAt ? `${new Date(org!.agbAcceptedAt).toLocaleString("de-DE")} (v${esc(org!.agbVersion || "")})` : "<em>nicht erfasst</em>"}</div>
        <div><strong>AVV akzeptiert:</strong> ${org!.avvAcceptedAt ? `${new Date(org!.avvAcceptedAt).toLocaleString("de-DE")} (v${esc(org!.avvVersion || "")})` : "<em>nicht erfasst</em>"}</div>
      </div>
      <div style="margin-top:.75rem;padding-top:.75rem;border-top:1px solid var(--gray-200)">
        <div style="font-size:.875rem;font-weight:600">Verknüpfte Schulen (${detail?.linkedSchools.length ?? 0}):</div>
        ${linkedSchoolsHtml}
      </div>
    </fieldset>` : `
    <div class="alert" style="background:var(--primary-50);color:var(--primary-dark);margin-top:1rem">
      <strong>Hinweis:</strong> Die Keycloak-Gruppe <code>/praktikumsstellen/&lt;slug&gt;</code> sowie ein eindeutiger Code <code>MV-XXXXXX</code> werden automatisch erstellt. Benutzer können anschließend über den Tab „Benutzer" hinzugefügt werden.
    </div>`}
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">${isEdit ? "Speichern" : "Anlegen"}</button></div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const data = {
      name: (mb.querySelector("#f-name") as HTMLInputElement).value.trim(),
      category: (mb.querySelector("#f-category") as HTMLSelectElement).value,
      contact_person: (mb.querySelector("#f-contact") as HTMLInputElement).value.trim(),
      street: (mb.querySelector("#f-street") as HTMLInputElement).value.trim(),
      house_no: (mb.querySelector("#f-houseno") as HTMLInputElement).value.trim(),
      plz: (mb.querySelector("#f-plz") as HTMLInputElement).value.trim(),
      ort: (mb.querySelector("#f-ort") as HTMLInputElement).value.trim(),
      email: (mb.querySelector("#f-email") as HTMLInputElement).value.trim(),
      phone: (mb.querySelector("#f-phone") as HTMLInputElement).value.trim(),
    };
    if (!data.name) { alert("Name ist erforderlich"); return; }
    if (!data.plz) { alert("PLZ ist erforderlich"); return; }
    if (!data.ort) { alert("Ort ist erforderlich"); return; }
    if (!data.email) { alert("E-Mail ist erforderlich"); return; }
    try {
      if (isEdit) {
        await api.admin.practices.update(org!.id, data);
      } else {
        await api.admin.practices.create(data);
      }
      closeModal();
      await load();
      renderTable(document.getElementById("main-content")!);
    } catch (err: any) {
      alert("Fehler: " + err.message);
    }
  });

  (mb.querySelector("#f-name") as HTMLInputElement).focus();
}

async function openUsersModal(org: PracticeOrgAdmin) {
  let users: PracticeUser[] = [];
  try { users = await api.admin.practices.users.list(org.id); } catch { /* ignore */ }

  const renderUserRows = () => {
    if (users.length === 0) return `<tr><td colspan="5" class="empty-state">Keine Benutzer vorhanden</td></tr>`;
    return users.map(u => `
      <tr>
        <td style="font-weight:500">${esc(u.firstName)} ${esc(u.lastName)}</td>
        <td class="text-muted">${esc(u.email)}</td>
        <td><span class="badge ${u.role === "praktikumsstelle-admin" ? "badge-primary" : ""}">${esc(ROLE_LABELS[u.role] || u.role)}</span></td>
        <td>${u.enabled
          ? `<span style="color:var(--success)">Aktiv</span>`
          : `<span style="color:var(--danger)">Deaktiviert</span>`}</td>
        <td style="white-space:nowrap">
          <button class="btn btn-sm pu-edit" data-uid="${u.id}">Bearbeiten</button>
          <button class="btn btn-sm pu-toggle" data-uid="${u.id}">${u.enabled ? "Deaktivieren" : "Aktivieren"}</button>
          <button class="btn btn-sm pu-pw" data-uid="${u.id}">Passwort</button>
          <button class="btn btn-sm btn-danger pu-del" data-uid="${u.id}">X</button>
        </td>
      </tr>`).join("");
  };

  openModal(`Benutzer: ${esc(org.name)}`, `
    <div style="margin-bottom:.75rem"><button class="btn btn-primary btn-sm" id="pu-add">+ Neuer Benutzer</button></div>
    <div class="table-wrap"><table><thead><tr>
      <th>Name</th><th>E-Mail</th><th>Rolle</th><th>Status</th><th style="width:280px">Aktionen</th>
    </tr></thead><tbody id="pu-tbody">${renderUserRows()}</tbody></table></div>
    <div class="form-actions" style="margin-top:1rem"><button class="btn btn-secondary" id="pu-close">Schließen</button></div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#pu-close")!.addEventListener("click", closeModal);
  mb.querySelector("#pu-add")!.addEventListener("click", () => {
    closeModal();
    openUserForm(org);
  });

  mb.querySelectorAll(".pu-edit").forEach(btn => btn.addEventListener("click", () => {
    const u = users.find(x => x.id === (btn as HTMLElement).dataset.uid);
    if (u) { closeModal(); openUserForm(org, u); }
  }));
  mb.querySelectorAll(".pu-toggle").forEach(btn => btn.addEventListener("click", async () => {
    const uid = (btn as HTMLElement).dataset.uid!;
    const u = users.find(x => x.id === uid);
    if (!u) return;
    try {
      await api.admin.practices.users.toggle(org.id, uid, !u.enabled);
      closeModal();
      await openUsersModal(org);
    } catch (err: any) { alert("Fehler: " + err.message); }
  }));
  mb.querySelectorAll(".pu-pw").forEach(btn => btn.addEventListener("click", () => {
    const uid = (btn as HTMLElement).dataset.uid!;
    const u = users.find(x => x.id === uid);
    if (u) { closeModal(); openResetPwModal(org, u); }
  }));
  mb.querySelectorAll(".pu-del").forEach(btn => btn.addEventListener("click", async () => {
    const uid = (btn as HTMLElement).dataset.uid!;
    const u = users.find(x => x.id === uid);
    if (!u || !confirm(`Benutzer „${u.email}" wirklich löschen?`)) return;
    try {
      await api.admin.practices.users.delete(org.id, uid);
      closeModal();
      await openUsersModal(org);
    } catch (err: any) { alert("Fehler: " + err.message); }
  }));
}

function openUserForm(org: PracticeOrgAdmin, u?: PracticeUser) {
  const isEdit = !!u;
  const roleOpts = Object.entries(ROLE_LABELS).map(([val, label]) =>
    `<option value="${val}" ${u?.role === val ? "selected" : ""}>${label}</option>`
  ).join("");

  openModal(isEdit ? "Benutzer bearbeiten" : `Neuer Benutzer (${esc(org.name)})`, `
    <div class="form-grid">
      <div class="form-group"><label>E-Mail *</label><input id="fu-email" type="email" value="${esc(u?.email || "")}" /></div>
      <div class="form-group"><label>Vorname *</label><input id="fu-fn" value="${esc(u?.firstName || "")}" /></div>
      <div class="form-group"><label>Nachname *</label><input id="fu-ln" value="${esc(u?.lastName || "")}" /></div>
      <div class="form-group"><label>Rolle</label><select id="fu-role">${roleOpts}</select></div>
      ${!isEdit ? `<div class="form-group" style="grid-column:1/-1"><p style="font-size:.875rem;color:var(--gray-600);margin:0">Der Benutzer erhält nach dem Anlegen eine E-Mail zum Setzen seines Passworts.</p></div>` : ""}
    </div>
    <div class="form-actions"><button class="btn btn-secondary" id="fu-cancel">Abbrechen</button><button class="btn btn-primary" id="fu-save">Speichern</button></div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#fu-cancel")!.addEventListener("click", () => { closeModal(); openUsersModal(org); });
  mb.querySelector("#fu-save")!.addEventListener("click", async () => {
    const email = (mb.querySelector("#fu-email") as HTMLInputElement).value.trim();
    const firstName = (mb.querySelector("#fu-fn") as HTMLInputElement).value.trim();
    const lastName = (mb.querySelector("#fu-ln") as HTMLInputElement).value.trim();
    const role = (mb.querySelector("#fu-role") as HTMLSelectElement).value;
    if (!email) { alert("E-Mail ist ein Pflichtfeld"); return; }
    if (!firstName) { alert("Vorname ist ein Pflichtfeld"); return; }
    if (!lastName) { alert("Nachname ist ein Pflichtfeld"); return; }
    try {
      if (isEdit) {
        await api.admin.practices.users.update(org.id, u!.id, { email, firstName, lastName, role });
      } else {
        const result = await api.admin.practices.users.create(org.id, { email, firstName, lastName, role });
        closeModal();
        if (result && (result as any).emailSent === false) {
          alert("Benutzer wurde erfolgreich erstellt. Bitte sende dem Benutzer über den „Passwort\"-Button in der Tabelle eine E-Mail, damit er sein Passwort setzen kann.");
        }
        await openUsersModal(org);
        return;
      }
      closeModal();
      await openUsersModal(org);
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}

function openResetPwModal(org: PracticeOrgAdmin, u: PracticeUser) {
  openModal("Passwort zurücksetzen", `
    <p>Es wird eine E-Mail an <strong>${esc(u.email)}</strong> gesendet, damit der Benutzer sich ein neues Passwort setzen kann.</p>
    <div class="form-actions"><button class="btn btn-secondary" id="fu-cancel">Abbrechen</button><button class="btn btn-primary" id="fu-save">Senden</button></div>`);

  const mb = modalBody();
  mb.querySelector("#fu-cancel")!.addEventListener("click", () => { closeModal(); openUsersModal(org); });
  mb.querySelector("#fu-save")!.addEventListener("click", async () => {
    try {
      const result = await api.admin.practices.users.resetPassword(org.id, u.id);
      if (result && result.tempPassword) {
        mb.innerHTML = `
          <p>Die E-Mail konnte nicht gesendet werden. Bitte teile dem Benutzer das folgende temporäre Passwort mit. Der Benutzer muss es beim nächsten Login ändern.</p>
          <div style="background:var(--gray-100);padding:.75rem 1rem;border-radius:.5rem;font-family:monospace;font-size:1.1rem;letter-spacing:.05em;margin:.5rem 0">${esc(result.tempPassword)}</div>
          <div class="form-actions"><button class="btn btn-primary" id="fu-close">Schließen</button></div>`;
        mb.querySelector("#fu-close")!.addEventListener("click", () => { closeModal(); openUsersModal(org); });
      } else {
        closeModal();
        await openUsersModal(org);
      }
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}

export async function render(el: HTMLElement) {
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Praktikumsstellen</h1>
        <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">Plattform-Verwaltung – Praktikumsstellen-Organisationen und ihre Benutzer</div>
      </div>
      <div style="display:flex;gap:.5rem"><button class="btn btn-primary" id="btn-add">+ Neue Praktikumsstelle</button></div>
    </div>
    <div style="margin-top:1.5rem">
      <div class="card table-wrap">
        <table>
          <thead><tr>
            <th>Name</th><th>Code</th><th>Kategorie</th><th>Ort</th>
            <th style="text-align:center">Status</th>
            <th style="text-align:center">Verknüpfte Schulen</th>
            <th>Erstellt</th>
            <th style="width:220px">Aktionen</th>
          </tr></thead>
          <tbody id="org-tbody"></tbody>
        </table>
      </div>
    </div>`;

  el.querySelector("#btn-add")!.addEventListener("click", () => openForm());

  try {
    await load();
    renderTable(el);
  } catch (err: any) {
    el.querySelector("#org-tbody")!.innerHTML = `<tr><td colspan="8" class="empty-state">Fehler beim Laden: ${esc(err.message)}</td></tr>`;
  }
}
